import { Mapping } from 'prosemirror-transform'
import { Node, ResolvedPos } from 'prosemirror-model'
import { NodeSelection, Selection } from 'prosemirror-state'
import { Slice } from 'prosemirror-model'

// ::- Gap cursor selections are represented using this class. Its
// `$anchor` and `$head` properties both point at the cursor position.
export class GapCursor extends Selection {
  // :: (ResolvedPos)
  // Create a gap cursor.
  constructor($pos: ResolvedPos) {
    super($pos, $pos)
  }

  map(doc: Node, mapping: Mapping): GapCursor | Selection {
    let $pos = doc.resolve(mapping.map(this.head))
    return GapCursor.valid($pos) ? new GapCursor($pos) : Selection.near($pos)
  }

  content() {
    return Slice.empty
  }

  eq(other: any) {
    return other instanceof GapCursor && other.head === this.head
  }

  toJSON() {
    return { type: 'gapcursor', pos: this.head }
  }

  static fromJSON(doc: Node, json: any) {
    if (typeof json.pos != 'number') throw new RangeError('Invalid input for GapCursor.fromJSON')
    return new GapCursor(doc.resolve(json.pos))
  }

  getBookmark() {
    return new GapBookmark(this.anchor)
  }

  static valid($pos: ResolvedPos) {
    if ($pos.nodeAfter?.type.groups.includes('textWrap') || $pos.nodeBefore?.type.groups.includes('textWrap'))
      return false
    let parent = $pos.parent
    if (parent.isTextblock) return false // NOTE: we removed the closedBefore/After check here, because we always want to get a gap cursor, even if e.g. the list item before the current one has a paragraph
    let override = parent.type.spec.allowGapCursor
    if (override != null) return override
    try {
      let deflt = parent.contentMatchAt($pos.index()).defaultType
      return deflt && deflt.isTextblock
    } catch (e) {
      return false
    }
  }

  static findFrom($pos: ResolvedPos, dir: number, mustMove: boolean): any {
    search: for (;;) {
      if (!mustMove && GapCursor.valid($pos)) return $pos
      let pos = $pos.pos,
        next = null
      // Scan up from this position
      for (let d = $pos.depth; ; d--) {
        let parent = $pos.node(d)
        if (dir > 0 ? $pos.indexAfter(d) < parent.childCount : $pos.index(d) > 0) {
          next = parent.child(dir > 0 ? $pos.indexAfter(d) : $pos.index(d) - 1)
          break
        } else if (d === 0) {
          return null
        }
        pos += dir
        let $cur = $pos.doc.resolve(pos)
        if (GapCursor.valid($cur)) return $cur
      }

      // And then down into the next node
      for (;;) {
        let inside: Node | null | undefined = dir > 0 ? next.firstChild : next.lastChild
        if (!inside) {
          if (next.isAtom && !next.isText && !NodeSelection.isSelectable(next)) {
            $pos = $pos.doc.resolve(pos + next.nodeSize * dir)
            mustMove = false
            continue search
          }
          break
        }
        next = inside
        pos += dir
        let $cur = $pos.doc.resolve(pos)
        if (GapCursor.valid($cur)) return $cur
      }

      return null
    }
  }
}

GapCursor.prototype.visible = false

Selection.jsonID('almanacGapcursor', GapCursor)

class GapBookmark {
  pos: number

  constructor(pos: number) {
    this.pos = pos
  }
  map(mapping: Mapping) {
    return new GapBookmark(mapping.map(this.pos))
  }
  resolve(doc: Node) {
    let $pos = doc.resolve(this.pos)
    return GapCursor.valid($pos) ? new GapCursor($pos) : Selection.near($pos)
  }
}
